import React, { useRef } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { navigate } from 'gatsby'
import styled from 'styled-components'
import {
  Box,
  P,
  Caption,
  H1,
  H2,
  Header,
  Button,
  Modal,
  Dropdown,
  DownshiftProvider,
  FileUploader,
  FileUploaderFormik,
  LoadingSpinner,
} from '@allied-solutions/affinity'
import { ArrowRight } from '@allied-solutions/affinity-react-icons/standard'

import {
  uploadInsuranceDocuments,
  submitInsuranceDocument,
  useLogTransaction,
} from '../../hooks'
import { getRequiredCoverageTypeLabel } from '../../utils/requiredCoverageTypeLabels'

const UPLOAD_MODAL_ID = 'Modal--DashboardUploadInsurance'
const UPLOAD_MODAL_LABEL_ID = `AffinityModal__${UPLOAD_MODAL_ID}__Label`
const UPLOAD_MODAL_ACCEPTED_TYPES = 'application/pdf, image/tiff, image/jpeg'

const formatUploadItemLabel = (referenceId, coverageType) =>
  `${referenceId} / ${getRequiredCoverageTypeLabel(coverageType)}`

const getUploadCoverageOptions = (coverageRequirementsData, borrowerData) => {
  const accounts = Array.isArray(coverageRequirementsData)
    ? coverageRequirementsData
    : []
  const optionsFromCoverage = accounts
    .filter(account => account?.ReferenceID)
    .map(account => {
      const referenceId = String(account.ReferenceID)
      return {
        value: referenceId,
        label: formatUploadItemLabel(
          referenceId,
          account.RequiredCoverageType
        ),
      }
    })

  if (optionsFromCoverage.length > 0) return optionsFromCoverage

  if (borrowerData?.ReferenceID) {
    const referenceId = String(borrowerData.ReferenceID)
    return [
      {
        value: referenceId,
        label: formatUploadItemLabel(
          referenceId,
          borrowerData.RequiredCoverageType
        ),
      },
    ]
  }

  return []
}

const uploadModalValidationSchema = Yup.object().shape({
  selectedItem: Yup.string()
    .nullable()
    .required('Please select an item to associate with the upload.'),
  files: Yup.array().min(1, 'Please attach at least one file.'),
})

const StyledModal = styled(Modal)`
  & .upload-modal-dialog {
    max-height: none;
  }
`

const StyledModalTitle = styled(H1)`
  margin-bottom: ${props => props.theme.space[1]};
`

const StyledButton = styled(Button)`
  background-color: ${props => props.theme.colors.background.brandMuted};
  color: ${props => props.theme.colors.text.brand};
  & svg {
    color: ${props => props.theme.colors.icons.brand};
  }
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.background.brandLight};
  }
`

const getLogRequestOverridesFromBorrower = borrowerData => {
  if (!borrowerData) return {}
  const isMortgage = borrowerData.contractType === 'Mortgage'
  const isVehicle = borrowerData.SecondaryClassification === 'VEH'
  return {
    LenderCode: borrowerData.LenderCode ?? null,
    ReferenceId: borrowerData.ReferenceID ?? null,
    IsMortgage: isMortgage,
    IsAgent: false,
    IsVehicle: isVehicle,
  }
}

export const DashboardUploadInsuranceModal = ({
  borrowerData,
  coverageRequirementsData,
  selectedReferenceId,
  isOpen,
  setIsOpen,
  setShowSuccessMessage,
}) => {
  const submitFormRef = useRef(null)
  const closeModalRef = useRef(null)
  const { LogTransaction } = useLogTransaction()

  const coverageOptions = getUploadCoverageOptions(
    coverageRequirementsData,
    borrowerData
  )
  const items = coverageOptions.map(option => option.value)
  const labelsByReferenceId = Object.fromEntries(
    coverageOptions.map(option => [option.value, option.label])
  )
  const getItemLabel = referenceId =>
    labelsByReferenceId[referenceId] ?? String(referenceId ?? '')

  const initialSelectedItem = (() => {
    if (selectedReferenceId == null) return items[0] ?? null
    const normalizedSelectedId = String(selectedReferenceId)
    return items.includes(normalizedSelectedId)
      ? normalizedSelectedId
      : items[0] ?? null
  })()

  return (
    <StyledModal
      id={UPLOAD_MODAL_ID}
      isOpen={isOpen}
      primaryAction={() => submitFormRef.current?.()}
      secondaryAction={() => closeModalRef.current?.()}
    >
      <Modal.Overlay />
      <Modal.Dialog className="upload-modal-dialog">
        <Formik
          key={isOpen ? `open-${initialSelectedItem}` : 'closed'}
          initialValues={{
            selectedItem: initialSelectedItem,
            files: [],
          }}
          validationSchema={uploadModalValidationSchema}
          validateOnMount
          onSubmit={async (
            values,
            { setSubmitting, resetForm, setFieldError }
          ) => {
            try {
              const formPayload = {
                ReferenceID:
                  values.selectedItem === null
                    ? ''
                    : String(values.selectedItem),
                MustAttachProofOfInsurance:
                  borrowerData?.MustAttachProofOfInsurance ?? false,
                IsMortgage: borrowerData?.contractType === 'Mortgage',
                IsAgent: false,
                LenderID:
                  borrowerData?.LenderID === null
                    ? ''
                    : String(borrowerData.LenderID),
                LenderCode:
                  borrowerData?.LenderCode === null
                    ? ''
                    : String(borrowerData.LenderCode),
                PolicyholderEmailAddress: '',
              }
              const args = await uploadInsuranceDocuments(
                values.files,
                formPayload
              )
              await submitInsuranceDocument(formPayload, args)
              const logOverrides = getLogRequestOverridesFromBorrower(
                borrowerData
              )
              await LogTransaction({ type: 'insurance' }, logOverrides)
              if (args?.length > 0) {
                await LogTransaction(
                  { type: 'attach', files: args.map(x => x.originalName) },
                  logOverrides
                )
              }
              resetForm()
              setIsOpen(false)
              setShowSuccessMessage(true)
            } catch (error) {
              if (error?.status === 503) {
                navigate('/systems-down/')
                return
              }
              const message =
                error?.message || 'Upload failed. Please try again.'
              setFieldError('files', [message])
            } finally {
              setSubmitting(false)
            }
          }}
        >
          {({
            values,
            errors,
            setFieldValue,
            handleSubmit,
            resetForm,
            isSubmitting,
            isValid,
            submitCount,
          }) => {
            submitFormRef.current = handleSubmit
            closeModalRef.current = () => {
              resetForm()
              setIsOpen(false)
            }
            return (
              <Form noValidate onSubmit={handleSubmit}>
                <Header textAlign="center">
                  <StyledModalTitle
                    id={UPLOAD_MODAL_LABEL_ID}
                    typeStyle="titleSmall.bold"
                  >
                    Update Insurance
                  </StyledModalTitle>
                </Header>
                <P typeStyle="bodyMedium.regular" color="text.secondary">
                  Upload the insurance documents for this loan.
                </P>
                <Box minWidth="300px">
                  <DownshiftProvider.Select
                    id="Dropdown--uploadModal"
                    items={items}
                    selectedItem={values.selectedItem}
                    onSelectedItemChange={({ selectedItem: next }) => {
                      if (next != null) setFieldValue('selectedItem', next)
                    }}
                  >
                    <Dropdown>
                      <Dropdown.Label mb="md">Choose an item</Dropdown.Label>
                      <Dropdown.Trigger>
                        {values.selectedItem
                          ? getItemLabel(values.selectedItem)
                          : 'Select one'}
                      </Dropdown.Trigger>
                      <Dropdown.Menu>
                        {items.map((item, index) => (
                          <Dropdown.MenuItem
                            item={item}
                            index={index}
                            key={`Dropdown--uploadModal__MenuItem--${item}`}
                          >
                            {getItemLabel(item)}
                          </Dropdown.MenuItem>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </DownshiftProvider.Select>
                  {submitCount > 0 && errors.selectedItem && (
                    <Caption display="flex" color="error" mt={2}>
                      {errors.selectedItem}
                    </Caption>
                  )}
                  <Box mt="xl">
                    <FileUploaderFormik
                      name="files"
                      id="uploadModal-fileUpload"
                      accept={UPLOAD_MODAL_ACCEPTED_TYPES}
                      multiple
                    >
                      {values.files?.length > 0 && (
                        <>
                          <H2
                            typeStyle="bodyMedium.bold"
                            textAlign="left"
                            mb={4}
                            mt={10}
                          >
                            Selected files
                          </H2>
                          <FileUploader.FilesList />
                        </>
                      )}
                    </FileUploaderFormik>
                  </Box>
                  {borrowerData.SecondaryClassification === 'VEH' ? (
                    <StyledButton
                      onClick={() => {
                        setIsOpen(false)
                        setTimeout(() => navigate('/enter-reference-id/'), 0)
                      }}
                      minWidth="100%"
                      mt="2xl"
                      icon={ArrowRight}
                      iconPos="right"
                    >
                      Fill out manually instead
                    </StyledButton>
                  ) : null}
                </Box>
                <Modal.Footer>
                  <Modal.SecondaryButton type="button">
                    Cancel
                  </Modal.SecondaryButton>
                  <Modal.PrimaryButton
                    type="button"
                    disabled={isSubmitting || !isValid}
                  >
                    {isSubmitting ? <LoadingSpinner /> : 'Upload'}
                  </Modal.PrimaryButton>
                </Modal.Footer>
              </Form>
            )
          }}
        </Formik>
      </Modal.Dialog>
    </StyledModal>
  )
}
